import { getSubWithoutPrefix, useAuthState } from '@/auth/auth'
import { useHasSomePermission } from '@/auth/permissions'
import { useLDflags } from '@/launchdarkly/Launchdarkly'
import { useTrackEvent } from '@/mixpanel/useTrackEvent'
import { useReadHubSpotIntegrationStatus } from '@/modules/integrations/hubspot/useReadHubSpotIntegrationStatus'
import { useReadSalesforceIntegrationStatus } from '@/modules/integrations/salesforce/useReadSalesforceIntegrationStatus'
import { useChangeSpaceOwnerModalHook } from '@/ui-components/modal/space/ChangeSpaceOwner'
import { TSpace } from '@valuecase/common'
import { Button2 } from '@valuecase/ui-components/src/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@valuecase/ui-components/src/components/ui/dropdown-menu'
import { useMemo } from 'react'
import { LinkToHubSpotTrigger } from './linkSpaceToHubSpotFlow/LinkToHubSpotTrigger'
import { LinkToSalesforceTrigger } from './linkSpaceToSalesforceFlow/LinkToSalesforceTrigger'
import { SpaceArchivingTrigger } from './spaceArchivingFlow/SpaceArchivingTrigger'

interface SpaceContextMenuProps {
  space: TSpace
  trigger?: React.ReactNode
  onCopyLinkToSpace: (id: string, token?: string) => void
  showCreateTemplateFromSpaceOverlay: (spaceId: string) => void
  setSpaceSelectedForTeamEditing: (space: any) => void
  selectSpaceForDeletion?: (space: any) => void
  setShowDeleteSpaceModal?: (show: boolean) => void
  getUrlWithToken: (spaceId: string, token?: string, showActivityFeed?: boolean) => string
}

export const SpaceContextMenu = ({
  space,
  trigger,
  onCopyLinkToSpace,
  showCreateTemplateFromSpaceOverlay,
  setSpaceSelectedForTeamEditing,
  selectSpaceForDeletion,
  setShowDeleteSpaceModal,
  getUrlWithToken,
}: SpaceContextMenuProps) => {
  const { trackEvent } = useTrackEvent()
  const auth = useAuthState()
  const { flags, isFetched: isFlagsFetched } = useLDflags()
  const { hubSpotIntegrationStatus } = useReadHubSpotIntegrationStatus()
  const hasHubSpotIntegration = hubSpotIntegrationStatus === 'enabled'
  const { salesforceIntegrationStatus } = useReadSalesforceIntegrationStatus()
  const hasSalesforceIntegration = salesforceIntegrationStatus?.status === 'enabled'
  const { show: showChangeSpaceOwnerModal } = useChangeSpaceOwnerModalHook()
  const hasPermissionToChangeSpaceOwner = useHasSomePermission(
    'USERS_MANAGE',
    'SPACES_ALL_ADMINISTRATION',
  )
  const isAllowedToSaveAsTemplate = useHasSomePermission(
    'SPACES_ALL_ADMINISTRATION',
    'TEMPLATES_MANAGE',
  )
  const hasPermissionToArchive = useHasSomePermission('SPACES_ALL_ADMINISTRATION')

  const isAllowedToChangeSpaceOwnerOrDeleteSpace = useMemo(
    () => (space: any) => {
      return hasPermissionToChangeSpaceOwner || space.ownerId === getSubWithoutPrefix(auth)
    },
    [hasPermissionToChangeSpaceOwner, auth],
  )

  const isAllowedToArchiveSpace = useMemo(
    () => (space: any) =>
      (space.ownerId && space.ownerId === getSubWithoutPrefix(auth)) || hasPermissionToArchive,
    [auth, hasPermissionToArchive],
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger ? (
          trigger
        ) : (
          <Button2
            leadingIcon='ellipsis-vertical'
            size='small'
            variant='plain'
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
          />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-56'>
        <DropdownMenuItem
          icon='log-in-outline'
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            window.location.href = getUrlWithToken(space.rootNodeId)
          }}
        >
          Open
        </DropdownMenuItem>

        <DropdownMenuItem
          icon='open-outline'
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            trackEvent({
              event: 'spaces-open-new_tab',
              eventProperties: {
                indexPageName: 'Space List',
              },
            })
            window.open(getUrlWithToken(space.rootNodeId), '_blank', 'noopener,noreferrer')
          }}
        >
          Open in new tab
        </DropdownMenuItem>

        <DropdownMenuItem
          icon='c_link'
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            trackEvent({
              event: 'spaces-copy_link-options_menu',
              eventProperties: {
                indexPageName: 'Space List',
              },
            })
            onCopyLinkToSpace(space.rootNodeId, space.authToken)
          }}
        >
          Copy space link
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {flags && flags['teams-management'] && (
          <DropdownMenuItem
            icon='create-outline'
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              trackEvent({
                event: 'spaces-edit-teams',
                eventProperties: {
                  spaceId: space.id,
                },
              })
              setSpaceSelectedForTeamEditing(space)
            }}
          >
            Edit teams
          </DropdownMenuItem>
        )}

        {isAllowedToSaveAsTemplate && (
          <DropdownMenuItem
            icon='grid-outline'
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              trackEvent({
                event: 'spaces-save_as_template-open_modal',
                eventProperties: {
                  spaceId: space.id,
                },
              })
              showCreateTemplateFromSpaceOverlay(space.rootNodeId)
            }}
          >
            Save as template
          </DropdownMenuItem>
        )}

        {isFlagsFetched &&
          flags &&
          flags['link-space-to-hub-spot-space-list-action'] &&
          hasHubSpotIntegration && <LinkToHubSpotTrigger space={space} />}

        {hasSalesforceIntegration && <LinkToSalesforceTrigger space={space} />}
        {isAllowedToChangeSpaceOwnerOrDeleteSpace(space) ? (
          <DropdownMenuItem
            icon='shuffle-outline'
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              showChangeSpaceOwnerModal(
                space.rootNodeId,
                space.companyName,
                space.ownerId
                  ? {
                      id: space.ownerId,
                      fullName: space.owner?.firstName + ' ' + space.owner?.lastName,
                    }
                  : undefined,
              )
            }}
          >
            {space.ownerId ? 'Change space owner' : 'Set space owner'}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem icon='shuffle-outline' disabled>
            Change space owner
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {isFlagsFetched && flags && flags.archiving && isAllowedToArchiveSpace(space) && (
          <SpaceArchivingTrigger
            spaceId={space.rootNodeId}
            companyName={space.companyName}
            isCurrentlyArchived={!!space.archived}
            spaceUrlWithToken={getUrlWithToken(space.id, space.authToken)}
          />
        )}

        {selectSpaceForDeletion && setShowDeleteSpaceModal && (
          <>
            {isAllowedToChangeSpaceOwnerOrDeleteSpace(space) ? (
              <DropdownMenuItem
                icon='trash-outline'
                destructive
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  trackEvent({
                    event: 'spaces-delete-open_modal',
                    eventProperties: {
                      indexPageName: 'Space List',
                      spaceId: space?.id || 'unknownSpaceId',
                    },
                  })
                  selectSpaceForDeletion(space)
                  setShowDeleteSpaceModal(true)
                }}
                className='text-warning-s5'
              >
                Delete space
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem icon='trash-outline' disabled destructive>
                Delete space
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
